import { useEffect, useRef } from "react";

/**
 * Light polling hook with visibility awareness.
 *
 * Behaviour:
 *   • Runs `fn` on mount.
 *   • Reruns every `intervalMs` while document is visible.
 *   • Skips ticks while the tab is hidden (saves CPU/network).
 *   • Reruns immediately when the tab becomes visible again.
 *
 * Default interval is intentionally generous (60s) — fast enough for "near
 * realtime" dashboard panels, slow enough to avoid hammering the API.
 */
export default function useSmartPoll(fn, intervalMs = 60000) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let timer = null;
    let cancelled = false;

    const run = () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      try { fnRef.current(); } catch (_) { /* ignore */ }
    };

    // Initial fetch
    run();

    const tick = () => { run(); };
    timer = setInterval(tick, intervalMs);

    const onVisibility = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
